import { HTMLAttributes, ReactNode, ElementType, forwardRef } from 'react';

import * as CSS from 'csstype';
import styled from 'styled-components';

import { theme, Spacing } from '@styles/theme';

export const Stack = forwardRef<HTMLElement, StackProps>((props, ref) => {
  const {
    orientation = 'vertical',
    isReverse = false,
    wrap = false,
    space = 0,
    align,
    justify,
    as = 'div',
    dataCy,
    ...rest
  } = props;
  return (
    <Container
      ref={ref}
      as={as}
      $orientation={orientation}
      $space={space}
      $wrap={wrap}
      $align={align}
      $justify={justify}
      $isReverse={isReverse}
      data-cy={dataCy}
      {...rest}
    />
  );
});

export const Horizontal = forwardRef<HTMLElement, Omit<StackProps, 'orientation'>>((props, ref) => {
  return <Stack orientation="horizontal" {...props} ref={ref} />;
});

const Container = styled.div<{
  $orientation: Orientation;
  $isReverse?: boolean;
  $space: Spacing;
  $wrap: boolean;
  $align?: StackProps['align'];
  $justify?: StackProps['justify'];
}>((p) => ({
  gap: theme.space[p.$space],
  alignItems: p.$align,
  justifyContent: p.$justify,
  display: 'flex',
  flexDirection:
    p.$orientation === 'vertical'
      ? `column${p.$isReverse ? '-reverse' : ''}`
      : `row${p.$isReverse ? '-reverse' : ''}`,
  ...(p.$wrap && { flexWrap: 'wrap' }),
  ...(p.$align && { alignItems: p.$align }),
  ...(p.$justify && { justifyContent: p.$justify }),
}));

type Orientation = 'vertical' | 'horizontal';

export interface StackProps extends HTMLAttributes<any> {
  orientation?: Orientation;
  isReverse?: boolean;
  children: ReactNode;
  space?: Spacing;
  wrap?: boolean;
  className?: string;
  align?: CSS.Property.AlignItems;
  justify?: CSS.Property.JustifyContent;
  as?: ElementType;
  dataCy?: string;
}
